import './payment-method-update-sheet.scss';

import { ReactElement, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import {
  FormSubmit,
  FormSubmitProps,
} from '@sola/ark-core/molecules/form/form.js';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  SupporterAchFieldsEnum,
  SupporterCCFieldsEnum,
} from '@sola/schema/dotcom/supporters/paymentMethods.js';
import { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.js';
import { userErrorPaymentMethodUpdate } from '@sola/shared-utils/errors/supporters.js';
import { usePaymentMethodUpdate } from '@sola/supporter-sdk/hooks.js';

import { LocalAlert } from '../../molecules/local-alerts/local-alerts';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider';
import { PaymentMethodUpdateForm } from '../../molecules/payment-method-update-form/payment-method-update-form';
import { LocalAlertConfig } from '../../types/localeAlerts';
import {
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getErrorTranslationKeys } from '../../utils/errors';

type PaymentMethodUpdateSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;

const PaymentMethodUpdateSheetFooter: React.FC<
  PaymentMethodUpdateSheetFooterProps
> = ({ isBusy }) => {
  const { t } = useTranslation('my');

  return (
    <SheetFooter>
      <SheetActions>
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            fullWidth
          >
            <button type="button">
              {t('updatePaymentMethod.sheetAction.cancel')}
            </button>
          </ButtonStyle>
        </SheetClose>

        <FormSubmit
          iconBefore={<ArkIcon iconName="lock-01" />}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('updatePaymentMethod.sheetAction.update')}
        </FormSubmit>
      </SheetActions>
    </SheetFooter>
  );
};

export interface PaymentMethodUpdateProps {
  openTrigger: ReactElement;
  paymentMethod: GraphQLSupporterPaymentMethod;
}

export const PaymentMethodUpdateSheet: React.FC<PaymentMethodUpdateProps> = ({
  openTrigger,
  paymentMethod,
}) => {
  const { t } = useTranslation('my');
  const [isOpen, setIsOpen] = useState(false);
  const [sheetAlerts, setSheetAlerts] = useState<LocalAlertConfig[]>([]);
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const updatePaymentMethod = usePaymentMethodUpdate();

  const removeSheetAlert = (id: string) =>
    setSheetAlerts(sheetAlerts.filter((alert) => alert.id !== id));

  const updateAch = (
    data: FieldValues,
  ): GraphQLSupporterPaymentMethod | undefined => {
    if (!paymentMethod.ach) {
      return;
    }

    return {
      ...paymentMethod,
      accountNickname: data[SupporterAchFieldsEnum.nickname],
      ach: {
        ...paymentMethod.ach,
        accountName: data[SupporterAchFieldsEnum.accountName],
      },
    };
  };

  const updateCard = (
    data: FieldValues,
  ): GraphQLSupporterPaymentMethod | undefined => {
    if (!paymentMethod.creditCard) {
      return;
    }

    const expirationDate = getLongExpiryDate(
      data[SupporterCCFieldsEnum.expirationDate],
    );

    return {
      ...paymentMethod,
      accountNickname: data[SupporterCCFieldsEnum.nickname],
      creditCard: {
        ...paymentMethod.creditCard,
        cardholderName: data[SupporterCCFieldsEnum.nameOnCard],
        expirationDate,
      },
    };
  };

  const handleSubmit = async (data: FieldValues) => {
    setSheetAlerts([]);
    clearAlerts();

    try {
      const updatedPaymentMethod = paymentMethod.ach
        ? updateAch(data)
        : updateCard(data);

      if (!updatedPaymentMethod) {
        throw userErrorPaymentMethodUpdate();
      }

      await updatePaymentMethod.mutateAsync(updatedPaymentMethod);

      addAlerts(
        createPaymentMethodSuccessAlert(
          updatedPaymentMethod,
          'updatePaymentMethod.success.action',
        ),
      );
      setIsOpen(false);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodUpdate,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger>{openTrigger}</SheetTrigger>
      <SheetContent title={t('updatePaymentMethod.form.title')}>
        <SheetHeader>{t('updatePaymentMethod.form.title')}</SheetHeader>
        <SheetBody>
          <p className="body-sm md:body-md body-neutral-mild">
            {t('updatePaymentMethod.form.leadin')}
          </p>

          {sheetAlerts.length > 0 && (
            <div className="mt-comfy-sm md:mt-comfy-lg">
              {sheetAlerts.map((sheetAlert) => (
                <LocalAlert
                  key={sheetAlert.id}
                  alert={sheetAlert}
                  className="my-comfy-xs"
                  onClose={() => removeSheetAlert(sheetAlert.id)}
                />
              ))}
            </div>
          )}

          <Divider
            purpose="neutral"
            className="mt-comfy-lg mb-comfy-xl md:mt-comfy-xl"
          />

          <PaymentMethodUpdateForm
            paymentMethod={paymentMethod}
            onSubmit={handleSubmit}
          >
            <PaymentMethodUpdateSheetFooter
              isBusy={updatePaymentMethod.isPending}
            />
          </PaymentMethodUpdateForm>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
